import { useState, useEffect, useCallback } from 'react';
import type { NoteView } from '@/contexts/AppContext.js';

const NOTES_STORAGE_KEY = 'dexto-notes';

/**
 * Hook for managing notes with localStorage persistence.
 *
 * TODO: Sync with backend blob store for @ mention support
 *
 * To enable @ mentions in chats:
 * 1. Create POST /api/notes endpoint that stores notes as blob resources
 * 2. Notes should be stored with URI format: blob:note:{userId}:{noteId}
 * 3. Include metadata: { type: 'note', title, tags, mimeType: 'text/markdown' }
 * 4. This will make notes discoverable via /api/resources endpoint
 * 5. Chat input can then use @ autocomplete with resourceManager.list()
 */
export function useNotes() {
    const [notes, setNotes] = useState<NoteView[]>([]);
    const [loading, setLoading] = useState(true);

    // Load notes from localStorage
    const loadNotes = useCallback(() => {
        try {
            const stored = localStorage.getItem(NOTES_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setNotes(Array.isArray(parsed) ? parsed : []);
            }
        } catch (error) {
            console.error('Failed to load notes from localStorage:', error);
        }
    }, []);

    // Load on mount
    // TODO: Replace with API call to GET /api/notes
    useEffect(() => {
        loadNotes();
        setLoading(false);
    }, [loadNotes]);

    // Listen for external changes to notes (from event handlers, other tabs, etc.)
    useEffect(() => {
        const handleNotesChanged = () => {
            console.log('[useNotes] External notes change detected, reloading...');
            loadNotes();
        };

        window.addEventListener('notes-changed', handleNotesChanged);
        return () => window.removeEventListener('notes-changed', handleNotesChanged);
    }, [loadNotes]);

    // Save notes to localStorage whenever they change
    // TODO: Replace with debounced API call to PUT /api/notes/:id
    useEffect(() => {
        if (!loading) {
            try {
                localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
            } catch (error) {
                console.error('Failed to save notes to localStorage:', error);
            }
        }
    }, [notes, loading]);

    const createNote = useCallback((note: NoteView) => {
        setNotes((prev) => [note, ...prev]);
        return note;
    }, []);

    const updateNote = useCallback((id: string, updates: Partial<NoteView>) => {
        setNotes((prev) =>
            prev.map((note) =>
                note.id === id ? { ...note, ...updates, updatedAt: Date.now() } : note
            )
        );
    }, []);

    const deleteNote = useCallback((id: string) => {
        setNotes((prev) => prev.filter((note) => note.id !== id));
    }, []);

    const getNote = useCallback(
        (id: string) => {
            return notes.find((note) => note.id === id);
        },
        [notes]
    );

    return {
        notes,
        loading,
        createNote,
        updateNote,
        deleteNote,
        getNote,
    };
}
